import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from '@mui/material';
import { useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import * as fileApi from '../../lib/api/file';
import AskDialog from '../../components/common/dialog/AskDialog';
import NoticeFileList from '../../components/noticemng/NoticeFileList';
import { listNoticeFiles, unloadNoticeFiles } from '../../modules/noticeModule/noticeFiles';

const NoticeFileListContainer = ({ notice }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    //redux selection
    const { files, error, loading } = useSelector(
        ({ noticeModule, loading }) => ({
            files: noticeModule.noticeFiles.files,
            error: noticeModule.noticeFiles.error,
            loading: loading['notice/LIST_NOTICE_FILES'],
        }),
    );
    //useEfffect

    useEffect(() => {
        if (notice) {
            dispatch(listNoticeFiles('notice', notice.wrUuid));
        }

        //언마운트될때 리덕스에서 파트너 데이터 초기화
        return () => {
            dispatch(unloadNoticeFiles());
        };
    }, [dispatch, notice]);

    const apiRef = useGridApiRef();

    // 백엔드 API를 통한 파일 다운로드
    const handleDownload = async (fileUuid, fileNm) => {
        try {
            const response = await fileApi.downloadFile(fileUuid);
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileNm;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 60000);
            a.remove();
        } catch (err) {
            console.error('Download error:', err);
            enqueueSnackbar('파일 다운로드에 실패했습니다.', {
                variant: 'error',
            });
        }
    };

    const [removeId, setRemoveId] = useState(null);
    const handleDeleteClick = (id) => (event) => {
        event.stopPropagation();

        //remove 작업
        setVisible(true);
        setRemoveId(id);
    };

    const onRemove = async () => {
        try {
            const res = await fileApi.removeFile(removeId);
            if (res) {
                apiRef.current.updateRows([{ id: removeId, _action: 'delete' }]);
                enqueueSnackbar('삭제 성공', {
                    variant: 'success',
                });
            }
        } catch (err) {
            let message =
                typeof err.response !== 'undefined'
                    ? err.response.data.message
                    : err.message;
            enqueueSnackbar(message, {
                variant: 'error',
            });
        }
        setRemoveId(null);
    };

    const [visible, setVisible] = useState(false);

    const onCancle = () => {
        setVisible(false);
        setRemoveId(null);
    };

    const onConfirm = () => {
        setVisible(false);
        onRemove();
    };


    const columns = useMemo(
        () => [
            {
                field: 'id',
                headerName: 'ID',
                hide: true,
                type: 'string',
                editable: false,
                filterable: false,
            },
            {
                field: 'fileUuid',
                headerName: '파일코드',
                hide: true,
                type: 'string',
                editable: false,
                filterable: false,
            },
            {
                field: 'fileTyp',
                headerName: '파일타입',
                hide: true,
                type: 'string',
                editable: false,
                filterable: false,
            },
            {
                field: 'fileAttrVal1',
                headerName: '공지ID',
                hide: true,
                type: 'string',
                editable: false,
                filterable: false,
            },
            {
                field: 'fileAttrVal2',
                headerName: '파일 타입',
                hide: true,
                type: 'string',
                editable: false,
                filterable: false,
            },
            {
                field: 'fileAttrVal3',
                headerName: '파일참고2',
                hide: true,
                type: 'string',
                editable: false,
                filterable: false,
            },
            {
                field: 'fileNm',
                headerName: '파일명',
                minWidth: 400,
                flex: 1,
                editable: false,
                filterable: false,
                renderCell: (params) => (
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleDownload(params.row.fileUuid, params.row.fileNm)}
                        sx={{ cursor: 'pointer', textAlign: 'left' }}
                    >
                        {params.value}
                    </Link>
                ),
            },
            {
                field: 'fileDesc',
                headerName: '참고사항',
                minWidth: 400,
                flex: 1,
                type: 'string',
                editable: true,
            },

            {
                field: 'crtNm',
                headerName: '등록자',
                minWidth: 80,
                flex: 1,
                type: 'string',
                editable: false,
            },

            {
                field: 'crtDt',
                headerName: '등록일',
                minWidth: 200,
                flex: 1,
                type: 'string',
                editable: false,
            },

            {
                field: 'actions',
                type: 'actions',
                headerName: 'Actions',
                width: 100,
                cellClassName: 'actions',
                getActions: ({ id, row }) => {
                    return [
                        <GridActionsCellItem
                            icon={<CloudDownloadIcon />}
                            label="Download"
                            onClick={() => handleDownload(row.fileUuid, row.fileNm)}
                            color="primary"
                        />,
                        <GridActionsCellItem
                            icon={<DeleteIcon />}
                            label="Delete"
                            onClick={handleDeleteClick(id)}
                            color="inherit"
                        />,
                    ];
                },
            },
        ],
        [handleDownload],
    );

    return (
        <div>
            <NoticeFileList
                apiRef={apiRef}
                columns={columns}
                files={files}
                error={error}
                loading={loading}
                columnVisibilityModel={{
                    id: false,
                    fileUuid: false,
                    fileTyp: false,
                    fileAttrVal1: false,
                    fileAttrVal2: false,
                    fileAttrVal3: false,
                  }}
            />
            <AskDialog
                visible={visible}
                title="공지사항 첨부파일 삭제"
                description="선택한 첨부 파일을 삭제하시겠습니까?"
                confirmText="삭제"
                onConfirm={onConfirm}
                onCancle={onCancle}
            />
        </div>
    )
}

export default NoticeFileListContainer